/* global Word console Office */

export async function insertText(text: string) {
  // Write text to the document.
  try {
    await Word.run(async (context) => {
      const body = context.document.body;
      body.insertParagraph(text, Word.InsertLocation.end);
      await context.sync();
    });
  } catch (error) {
    console.error("insertText error:", error);
  }
}

export async function getDocumentFile(): Promise<Blob | null> {
  // Get the whole document as a .docx Blob via Office File API.
  // NOTE: Office allows at most 2 open File handles at a time. closeAsync()
  // is called in a finally block so the handle is always released, even on error.
  const file = await new Promise<Office.File>((resolve, reject) => {
    Office.context.document.getFileAsync(
      Office.FileType.Compressed,
      { sliceSize: 65536 },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value);
        } else {
          reject(result.error);
        }
      }
    );
  });

  try {
    // Read all slices and assemble into a single Blob.
    const slices: Uint8Array[] = [];
    for (let i = 0; i < file.sliceCount; i++) {
      const sliceData = await new Promise<Uint8Array>((resolve, reject) => {
        file.getSliceAsync(i, (sliceResult) => {
          if (sliceResult.status === Office.AsyncResultStatus.Succeeded) {
            // data is typed `any` by @types/office-js; for FileType.Compressed
            // Office returns a number[] or Uint8Array depending on the host version.
            // Array.from normalises both to a plain array before constructing Uint8Array.
            resolve(new Uint8Array(Array.from(sliceResult.value.data as ArrayLike<number>)));
          } else {
            reject(sliceResult.error);
          }
        });
      });
      slices.push(sliceData);
    }

    const totalLength = slices.reduce((sum, s) => sum + s.length, 0);
    const combined = new Uint8Array(totalLength);
    let offset = 0;
    for (const slice of slices) {
      combined.set(slice, offset);
      offset += slice.length;
    }

    return new Blob([combined], {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });
  } catch (error) {
    console.error("getDocumentFile error:", error);
    return null;
  } finally {
    // Always release the Office File handle regardless of success or failure.
    await new Promise<void>((resolve) => {
      file.closeAsync(() => {
        resolve();
      });
    });
  }
}

export async function getDocumentText(): Promise<string | null> {
  // Get the whole document text. Returns `null` ONLY on Word.run failure so
  // callers can distinguish "Word is broken — don't overwrite S3" from
  // "the user genuinely cleared the document — sync the empty state".
  try {
    return await Word.run(async (context) => {
      const body = context.document.body;
      context.load(body, "text");
      await context.sync();
      return body.text;
    });
  } catch (error) {
    console.error("getDocumentText error:", error);
    return null;
  }
}
